import type { IViewportDimensions } from "@/landing/landing-action-creators";
import type { IMediaType } from "@/models";
import { isIos } from "./detectors";
import { defined, isNumber } from "./variable-evaluation";

export function optimizedImage(pic: IMediaType) {
  return pic.st;
}

export function fitSizesInFrame(
  imageDimensions: readonly [number, number],
  frameDimensions: readonly [number, number]
): number[] {
  const [iw, ih] = imageDimensions;
  const [fw, fh] = frameDimensions;
  const scale = Math.min(fw / iw, fh / ih);
  const dimensions = {
    // this adjusts all pictures to fit within the height perfectly and read just size to width only
    w: iw * scale,
    h: ih * scale,
  };
  return [dimensions.w, dimensions.h];
}

export function fitSizesInFrameNoWhitespace(
  imageDimensions: readonly [number, number],
  frameDimensions: readonly [number, number]
): number[] {
  const [iw, ih] = imageDimensions;
  const [fw, fh] = frameDimensions;
  const scale = Math.max(fw / iw, fh / ih);
  const dimensions = {
    // this adjusts all pictures to fit within the height perfectly and read just size to width only
    w: iw * scale,
    h: ih * scale,
  };
  return [dimensions.w, dimensions.h];
}

export const DEFAULT_IMAGE_SRC = "https://static.alua.com/default-avatar.jpg";

export const DEFAULT_MEDIA: IMediaType = {
  public_id: "1",
  th: DEFAULT_IMAGE_SRC,
  st: DEFAULT_IMAGE_SRC,
  rt: DEFAULT_IMAGE_SRC,
};

const LOADING_IMAGE_SRC =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8++btfwAJWwPHyxsq2AAAAABJRU5ErkJggg=="; // 1px --placeholder-color

export const LOADING_MEDIA: IMediaType = {
  public_id: "1",
  th: LOADING_IMAGE_SRC,
  st: LOADING_IMAGE_SRC,
  rt: LOADING_IMAGE_SRC,
};

export function imageGalleryDimensions(
  isFullscreen: boolean,
  viewportDimensions: IViewportDimensions
): number[] {
  if (isFullscreen) {
    if (isIos) {
      // on webkit the inner sizes are changing with zoom level
      // so we need to take that into consideration
      // and try to get the original size
      const scale = defined(window["visualViewport"])
        ? (window["visualViewport"]!.scale ?? 1)
        : 1; // https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
      return [window.innerWidth * scale, window.innerHeight * scale];
    }
    return [window.innerWidth, window.innerHeight];
  }
  const width = document.documentElement.clientWidth;
  const height = document.documentElement.clientHeight;
  const screenFraction = 0.8;
  return [
    width,
    isFullscreen ? height : Math.min(height * screenFraction, 500),
  ];
}

export function getAspectHeight(
  width: number,
  height: number,
  wrapperWidth: number
) {
  if (
    !isNumber(width) ||
    width === 0 ||
    !isNumber(height) ||
    height === 0 ||
    !isNumber(wrapperWidth) ||
    wrapperWidth === 0
  ) {
    return "auto";
  }

  const aspectRatio = width / height;
  return (1 / aspectRatio) * wrapperWidth;
}
